import { useCallback, useEffect, useState } from "react"
import { Place } from "@googlemaps/google-maps-services-js/dist/common"
import { PlaceAutocompleteResult } from "@googlemaps/google-maps-services-js/dist/places/autocomplete"
import { Portal } from "@radix-ui/react-popover"
import { Loader2, MapPin } from "lucide-react"
import { ControllerRenderProps, UseFormReturn } from "react-hook-form"
import { useHotkeys } from "react-hotkeys-hook"
import { infer as zInfer } from "zod"

import {
  getGooglePlaceDetailsClientSide,
  getGooglePlacesClientSide,
} from "@/lib/client-api/google.client"
import { Button } from "@/components/ui/button"
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

import { ConsulationFormSchema } from "./ConsultationForm"

type AddressObj = zInfer<typeof ConsulationFormSchema>["addressObj"]

const emptyAddressObj: AddressObj = { address: "", city: "", state: "", zip: "" }

function formatAddressObj({ address, city, state, zip }: AddressObj) {
  return [address, city, [state, zip].filter(Boolean).join(" ")]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ")
}

interface PropsIf {
  form: UseFormReturn<zInfer<typeof ConsulationFormSchema>>
  field: ControllerRenderProps<zInfer<typeof ConsulationFormSchema>, "address">
  propStyle?: string
}

export default function FormAddressInput({ field, form, propStyle }: PropsIf) {
  const [open, setOpen] = useState(false)
  const [address, setAddress] = useState("")
  const [loading, setLoading] = useState(false)
  const [predictions, setPredictions] = useState<PlaceAutocompleteResult[]>([])
  const [highlight, setHighlight] = useState<PlaceAutocompleteResult | null>(
    null
  )
  // Manual entry: the user opted in, or the autocomplete API failed
  const [manual, setManual] = useState(false)
  const [autocompleteFailed, setAutocompleteFailed] = useState(false)
  const addressObj = form.watch("addressObj") ?? emptyAddressObj

  useEffect(() => {
    if (address.length > 0 && !manual) {
      if (predictions.length === 0) setLoading(true)

      const delayDebounceFn = setTimeout(loadPredictions, 200)
      return () => clearTimeout(delayDebounceFn)
    } else {
      setLoading(false)
    }
  }, [address])

  const loadPredictions = useCallback(async () => {
    if (predictions.length === 0) setLoading(true)

    let updated: PlaceAutocompleteResult[]
    try {
      ;({ predictions: updated } = await getGooglePlacesClientSide({
        search: address,
      }))
    } catch (err) {
      console.error(err)
      switchToManual(true)
      return
    }
    setPredictions(updated)
    setLoading(false)
    if (updated.length > 0) {
      setHighlight(updated[0])
    }
  }, [address, predictions])

  useHotkeys(
    "down",
    (e) => {
      if (predictions.length > 0 && open) {
        setHighlight((current) =>
          getNextOrPreviousPlace(
            predictions,
            current ? current.place_id : predictions[0].place_id,
            "down"
          )
        )
        e.preventDefault()
      }
    },
    { enableOnFormTags: true },
    [predictions, highlight, open]
  )

  useHotkeys(
    "up",
    (e) => {
      if (predictions.length > 0 && open) {
        setHighlight((current) =>
          getNextOrPreviousPlace(
            predictions,
            current ? current.place_id : predictions[0].place_id,
            "up"
          )
        )
        e.preventDefault()
      }
    },
    { enableOnFormTags: true },
    [predictions, highlight, open]
  )

  useHotkeys(
    "enter",
    (e) => {
      if (open) {
        if (highlight) handleSelect(highlight)
        e.preventDefault()
      }
    },
    { enableOnFormTags: true },
    [predictions, highlight, open]
  )

  // useEffect(() => {
  //   if (!open && predictions.length > 0) {
  //     handleSelect(predictions[0], false)
  //   }
  // }, [open, predictions])

  async function handleSelect(item: PlaceAutocompleteResult, update = true) {
    setLoading(true)
    let result: Place
    try {
      ;({ result } = await getGooglePlaceDetailsClientSide({
        place_id: item.place_id,
      }))
    } catch (err) {
      console.error(err)
      switchToManual(true)
      return
    }
    if (result.formatted_address && result.address_components) {
      if (update) {
        field.onChange({
          target: {
            value: result.formatted_address,
          },
        })
      }

      let street = ""
      let route = ""
      let city = ""
      let state = ""
      let zip = ""
      let address = ""

      result.address_components.forEach((comp: any) => {
        if (comp.types.includes("street_number")) street = comp.long_name
        if (comp.types.includes("route")) route = comp.long_name
        if (comp.types.includes("locality")) city = comp.long_name
        if (comp.types.includes("administrative_area_level_1"))
          state = comp.short_name
        if (comp.types.includes("postal_code")) zip = comp.long_name
      })

      form.clearErrors("addressObj")

      address = [street, route].join(" ").trim()

      form.setValue("addressObj", { address, city, state, zip })
    }

    setOpen(false)
    setTimeout(() => {
      setLoading(false)
    }, 500)
  }

  function switchToManual(failed: boolean) {
    if (failed) setAutocompleteFailed(true)
    setManual(true)
    setOpen(false)
    setLoading(false)
    // Carry over what they typed so far as the street line
    if (!addressObj.address && field.value) {
      updateManualField("address", field.value)
    }
  }

  function updateManualField(key: keyof AddressObj, value: string) {
    const next = {
      ...emptyAddressObj,
      ...form.getValues("addressObj"),
      [key]: value,
    }
    form.setValue("addressObj", next, {
      shouldValidate: form.formState.isSubmitted,
    })
    field.onChange(formatAddressObj(next))
  }

  function getNextOrPreviousPlace(
    places: PlaceAutocompleteResult[],
    highlightPlaceId: string,
    direction: "up" | "down"
  ): PlaceAutocompleteResult | null {
    const currentIndex = places.findIndex(
      (place) => place.place_id === highlightPlaceId
    )
    if (currentIndex === -1) {
      return places[0]
    }

    let newIndex: number

    if (direction === "up") {
      newIndex = (currentIndex - 1 + places.length) % places.length
    } else {
      newIndex = (currentIndex + 1) % places.length
    }

    return places[newIndex]
  }

  if (manual) {
    const errors = form.formState.errors.addressObj
    return (
      <FormItem className="col-span-1">
        <FormLabel className={propStyle}>Address</FormLabel>
        {autocompleteFailed && (
          <p className="text-xs text-carbon-400">
            Address search is unavailable. Please enter your address below.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <FormControl>
              <Input
                type="text"
                required
                autoComplete="address-line1"
                aria-label="Street address"
                placeholder="street address *"
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={addressObj.address}
                onChange={(e) => updateManualField("address", e.target.value)}
              />
            </FormControl>
            <ManualFieldError message={errors?.address?.message} />
          </div>
          <div className="col-span-2">
            <Input
              type="text"
              required
              autoComplete="address-level2"
              aria-label="City"
              placeholder="city *"
              value={addressObj.city}
              onChange={(e) => updateManualField("city", e.target.value)}
            />
            <ManualFieldError message={errors?.city?.message} />
          </div>
          <div className="col-span-1">
            <Input
              type="text"
              required
              autoComplete="address-level1"
              aria-label="State"
              placeholder="state *"
              value={addressObj.state}
              onChange={(e) => updateManualField("state", e.target.value)}
            />
            <ManualFieldError message={errors?.state?.message} />
          </div>
          <div className="col-span-1">
            <Input
              type="text"
              required
              inputMode="numeric"
              autoComplete="postal-code"
              aria-label="Zip code"
              placeholder="zip *"
              value={addressObj.zip}
              onChange={(e) => updateManualField("zip", e.target.value)}
            />
            <ManualFieldError message={errors?.zip?.message} />
          </div>
        </div>
        {!autocompleteFailed && (
          <Button
            type="button"
            variant="link"
            className="h-fit p-0 text-xs text-carbon-400"
            onClick={() => setManual(false)}
          >
            Search for your address instead
          </Button>
        )}
      </FormItem>
    )
  }

  return (
    <FormItem className="col-span-1">
      <FormLabel className={propStyle} >Address</FormLabel>
      <Popover open={open} modal={true}>
        <PopoverTrigger asChild>
          <FormControl>
            <Input
              type={"address"}
              required
              placeholder="enter your address *"
              {...field}
              onClick={(e) => {
                e.preventDefault()
              }}
              onFocus={(e) => {
                e.preventDefault()
              }}
              onChange={(e) => {
                setAddress(e.target.value)
                setOpen(true)
                field.onChange(e)
              }}
            />
          </FormControl>
        </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-fit text-carbon-400 text-sm p-0 max-w-[90vw] rounded"
            onOpenAutoFocus={(e) => {
              e.preventDefault()
            }}
          >
            {form.getFieldState("addressObj").error && (
              <p className="flex gap-2 items-center font-medium text-sm px-4 py-2 border-b border-carbon-100 text-red-500 bg-red-50">
             Required
            </p>
            )}
            <p className="flex gap-2 items-center font-medium text-sm px-4 py-2 border-b border-carbon-100">
              Select an address from the list.
            </p>
            {loading ? (
              <p className="flex gap-2 items-center font-medium p-4">
                Loading... <Loader2 className="size-4 animate-spin" />
              </p>
            ) : address.length === 0 ? (
              <></>
            ) : predictions.length === 0 ? (
              <p className="flex gap-2 items-center font-medium p-4">
                No address found... <MapPin className="size-4" />
              </p>
            ) : (
              <div className="flex flex-col">
                {predictions.map((prediction) => {
                  return (
                    <Button
                      className="justify-start  h-fit max-h-fit text-left py-3"
                      key={prediction.place_id}
                      onClick={() => {
                        handleSelect(prediction)
                      }}
                      onMouseOver={() => setHighlight(prediction)}
                      variant={
                        highlight?.place_id === prediction.place_id
                          ? "secondary"
                          : "ghost"
                      }
                    >
                      {prediction.description}
                    </Button>
                  )
                })}
              </div>
            )}
            <Button
              type="button"
              variant="link"
              className="h-fit w-full justify-start px-4 py-2 border-t border-carbon-100 text-xs text-carbon-400"
              onClick={() => switchToManual(false)}
            >
              {"Can't find your address? Enter it manually"}
            </Button>
          </PopoverContent>
      </Popover>
      <FormMessage></FormMessage>
      {form.getFieldState("addressObj").error && (
        <FormMessage>Please select a valid address.</FormMessage>
      )}
      <Button
        type="button"
        variant="link"
        className="h-fit p-0 text-xs text-carbon-400"
        onClick={() => switchToManual(false)}
      >
        Enter address manually
      </Button>
    </FormItem>
  )
}

function ManualFieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-sm font-medium text-red-500">{message}</p>
}
